/**
 * B-290 item 2 (2026-09-28): a finished search hands over to the page it saved.
 *
 * When the search screen switches to its saved /c/<slug> page, the shopper is
 * still on the SAME look at the same prices - not a new visit. The search
 * already recorded that look (its own shopper-ledger row, `search_completed`
 * and the My searches entry), so the saved page must not record a second
 * "page opened": that would count every search twice in the retailer demand
 * numbers and, for a signed-in shopper, feed the club robot's paid queue,
 * which only a real page open may feed (owner rule, 23 Sep). The walk-away
 * clock also carries on from the search screen instead of restarting, and a
 * shopper who already acted there is not later counted as walking away.
 *
 * One marker, in memory only: set by the search screen just before it
 * switches, read once by each saved-page recorder. A reload, a shared link or
 * a page opened later runs in a fresh page, so it is always a real open.
 */

export type HandoffReader = "record_view" | "walk_away";

interface Handoff {
  slug: string;
  /** When the switch began (ms since epoch). */
  startedAt: number;
  /** The search screen's walk-away clock, handed over when it unmounts. */
  viewStartedAt: number | null;
  maxScrollPct: number;
  /** The search screen already recorded this view's walk-away (at most one per view). */
  walkAwayRecorded: boolean;
  carried: boolean;
  taken: HandoffReader[];
}

export interface CarriedView {
  viewStartedAt: number | null;
  maxScrollPct: number;
  walkAwayRecorded: boolean;
}

/** A switch finishes in well under a second; anything older is not this switch. */
export const HANDOFF_VALID_MS = 15_000;

let pending: Handoff | null = null;

export function beginSearchHandoff(slug: string, now: number = Date.now()): void {
  pending = {
    slug,
    startedAt: now,
    viewStartedAt: null,
    maxScrollPct: 0,
    walkAwayRecorded: false,
    carried: false,
    taken: [],
  };
}

function live(now: number): Handoff | null {
  if (pending && now - pending.startedAt > HANDOFF_VALID_MS) pending = null;
  return pending;
}

/**
 * Called by a walk-away recorder as it unmounts. True (and the clock is
 * handed over) only for the recorder that was on screen when the switch
 * began - the search screen's. Every other exit is a real exit.
 */
export function carryWalkAwayClock(
  mountedAt: number,
  view: { viewStartedAt: number; maxScrollPct: number; walkAwayRecorded: boolean },
  now: number = Date.now(),
): boolean {
  const h = live(now);
  if (!h || h.carried || mountedAt > h.startedAt) return false;
  h.carried = true;
  h.viewStartedAt = view.viewStartedAt;
  h.maxScrollPct = view.maxScrollPct;
  h.walkAwayRecorded = view.walkAwayRecorded;
  return true;
}

/**
 * Called by a saved-page recorder on mount: is this mount the switch? Answers
 * once per reader, so a later visit to the same page records normally.
 */
export function takeSearchHandoff(
  slug: string,
  reader: HandoffReader,
  now: number = Date.now(),
): CarriedView | null {
  const h = live(now);
  if (!h || h.slug !== slug || h.taken.includes(reader)) return null;
  h.taken.push(reader);
  return {
    viewStartedAt: h.viewStartedAt,
    maxScrollPct: h.maxScrollPct,
    walkAwayRecorded: h.walkAwayRecorded,
  };
}

/**
 * B-290 item 2: is the stored copy of the page this search's own (or newer)?
 * Both times are the server's: the stored copy's `fetched_at` is the saved
 * result's own time (api cache.py `_effective_fetched_at`), and the search
 * result carries the same value. The screen's own start time is used only
 * when the result carries no time. A time written without a zone is UTC (the
 * api writes UTC), never the shopper's local time.
 */
export function savedCopyIsFromThisSearch(
  storedFetchedAt: string | null | undefined,
  resultFetchedAt: string | null | undefined,
  screenStartedAt: number,
): boolean {
  const stored = parseServerTime(storedFetchedAt);
  if (stored === null) return false;
  const own = parseServerTime(resultFetchedAt);
  return stored >= (own ?? screenStartedAt);
}

function parseServerTime(value: string | null | undefined): number | null {
  if (!value) return null;
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(value.trim());
  const ms = Date.parse(hasZone ? value : `${value.trim()}Z`);
  return Number.isFinite(ms) ? ms : null;
}
